class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  static currentlyOpenMenu = null;
  static isInitialized = false;

  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.menuLink = this.querySelector('.header__menu-link');
    this.summary = this.querySelector('summary');
    this.megaMenuContent = this.querySelector('.mega-menu__content');
    this.closeTimer = null;

    // Initialize global menu item hover listeners only once
    if (!HeaderMenu.isInitialized) {
      HeaderMenu.initializeGlobalHoverListeners();
      HeaderMenu.isInitialized = true;
    }

    // Prevent default summary click behavior
    this.summary.addEventListener('click', this.onSummaryClick.bind(this));

    // Handle keyboard navigation
    this.addEventListener('keydown', this.onKeyDown.bind(this));

    // Add hover functionality to the entire header-menu element
    this.addEventListener('mouseenter', this.onMouseEnter.bind(this));
    this.addEventListener('mouseleave', this.onMouseLeave.bind(this));

    // Also add hover listeners to the mega menu content itself
    if (this.megaMenuContent) {
      this.megaMenuContent.addEventListener('mouseenter', this.onMouseEnter.bind(this));
      this.megaMenuContent.addEventListener('mouseleave', this.onMouseLeave.bind(this));
    }
  }

  static initializeGlobalHoverListeners() {
    // Add hover listeners to all header menu items
    document.querySelectorAll('.header__menu-item').forEach((menuItem) => {
      menuItem.addEventListener('mouseenter', () => {
        // Find the parent header-menu element (if any)
        const parentHeaderMenu = menuItem.closest('header-menu');

        // If there's a currently open menu and it's not the parent of this menu item
        if (HeaderMenu.currentlyOpenMenu && HeaderMenu.currentlyOpenMenu !== parentHeaderMenu) {
          HeaderMenu.currentlyOpenMenu.closeInstantly();
        }
      });
    });
  }

  onSummaryClick(event) {
    // If the click target is the link, allow normal navigation
    if (event.target.closest('.header__menu-link')) {
      return; // Let the link handle navigation
    }

    // Prevent default details toggle behavior only if not clicking on link
    event.preventDefault();

    // If click is on summary (but not link), toggle menu
    this.toggleMenu();
  }

  onKeyDown(event) {
    // Handle Escape key to close menu
    if (event.key === 'Escape' && this.mainDetailsToggle.hasAttribute('open')) {
      event.preventDefault();
      this.close();
      this.menuLink.focus();
    }
  }

  toggleMenu() {
    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.close();
    } else {
      this.open();
    }
  }

  onMouseEnter() {
    // Clear any pending close timer
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    // Close any other currently open menu instantly
    if (HeaderMenu.currentlyOpenMenu && HeaderMenu.currentlyOpenMenu !== this) {
      HeaderMenu.currentlyOpenMenu.closeInstantly();
    }

    // Set this as the currently open menu and open it
    HeaderMenu.currentlyOpenMenu = this;
    this.open();
  }

  onMouseLeave() {
    // Add a delay before closing to allow moving to dropdown content
    this.closeTimer = setTimeout(() => {
      this.close();
      this.closeTimer = null;
    }, 300); // 300ms delay
  }

  closeInstantly() {
    // Clear any pending close timer
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    // Close immediately without timer
    this.mainDetailsToggle.removeAttribute('open');

    // Clear static reference if this was the currently open menu
    if (HeaderMenu.currentlyOpenMenu === this) {
      HeaderMenu.currentlyOpenMenu = null;
    }
  }

  open() {
    this.mainDetailsToggle.setAttribute('open', '');
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    // Clear any pending close timer
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    // Clear static reference if this was the currently open menu
    if (HeaderMenu.currentlyOpenMenu === this) {
      HeaderMenu.currentlyOpenMenu = null;
    }
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }

  disconnectedCallback() {
    // Clean up timer when component is removed
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    // Clear static reference if this was the currently open menu
    if (HeaderMenu.currentlyOpenMenu === this) {
      HeaderMenu.currentlyOpenMenu = null;
    }
  }
}

customElements.define('header-menu', HeaderMenu);
